import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { Joyride, EVENTS, STATUS } from 'react-joyride'
import type { Step, EventData, Controls } from 'react-joyride'
import { useAuth } from './AuthContext'
import { getPortalSteps } from '../tour/portalSteps'
import { recommendationSteps } from '../tour/recommendationSteps'
import { recommendationDetailSteps } from '../tour/recommendationDetailSteps'
import { quoteCreateSteps } from '../tour/quoteCreateSteps'
import { tourOptions, tourStyles } from '../tour/tourStyles'
import {
  hasSeenFirstLoginPrompt,
  isTourCompleted,
  markFirstLoginPromptSeen,
  markTourCompleted,
} from '../tour/tourStorage'

type TourName = 'portal' | 'recommendation' | 'recommendation-detail' | 'quote-create'

interface TourContextType {
  startTour: (name: TourName) => void
  maybeStartTour: (name: TourName) => void
  isRunning: boolean
  showFirstLoginPrompt: boolean
  handleFirstLoginStart: () => void
  handleFirstLoginDismiss: () => void
}

const TourContext = createContext<TourContextType>({
  startTour: () => {},
  maybeStartTour: () => {},
  isRunning: false,
  showFirstLoginPrompt: false,
  handleFirstLoginStart: () => {},
  handleFirstLoginDismiss: () => {},
})

export function useTour() {
  return useContext(TourContext)
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user, isAdmin, canManageUsers, isIntern, isOfficeSupport } = useAuth()
  const isConsultant = user?.role === 'consultant'
  const [run, setRun] = useState(false)
  const [steps, setSteps] = useState<Step[]>([])
  const [activeTour, setActiveTour] = useState<TourName | null>(null)
  const [showFirstLoginPrompt, setShowFirstLoginPrompt] = useState(false)

  useEffect(() => {
    if (user?.id && !hasSeenFirstLoginPrompt(user.id)) {
      setShowFirstLoginPrompt(true)
    }
  }, [user?.id])

  const stepsFor = useCallback((name: TourName): Step[] => {
    switch (name) {
      case 'portal':
        return getPortalSteps({ isAdmin, canManageUsers, isIntern, isOfficeSupport, isConsultant })
      case 'recommendation':
        return recommendationSteps
      case 'recommendation-detail':
        return recommendationDetailSteps
      case 'quote-create':
        return quoteCreateSteps
    }
  }, [isAdmin, canManageUsers, isIntern, isOfficeSupport, isConsultant])

  const startTour = useCallback((name: TourName) => {
    setActiveTour(name)
    setSteps(stepsFor(name))
    setRun(true)
  }, [stepsFor])

  // Fire a tour only if (a) no other tour is running, (b) the welcome modal
  // isn't blocking the screen, and (c) this user has never completed it.
  // Used by page-level auto-start so consultants get walked through new
  // surfaces without having to click anything.
  const maybeStartTour = useCallback((name: TourName) => {
    if (!user?.id || run || showFirstLoginPrompt) return
    if (isTourCompleted(user.id, name)) return
    startTour(name)
  }, [user?.id, run, showFirstLoginPrompt, startTour])

  const handleFirstLoginStart = useCallback(() => {
    if (user?.id) {
      markFirstLoginPromptSeen(user.id)
    }
    setShowFirstLoginPrompt(false)
    startTour('portal')
  }, [user?.id, startTour])

  const handleFirstLoginDismiss = useCallback(() => {
    if (user?.id) {
      markFirstLoginPromptSeen(user.id)
    }
    setShowFirstLoginPrompt(false)
  }, [user?.id])

  const handleEvent = useCallback((data: EventData, _controls: Controls) => {
    const { type, status } = data
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

    if (type === EVENTS.TOUR_END && finishedStatuses.includes(status)) {
      setRun(false)
      if (activeTour && user?.id) {
        markTourCompleted(user.id, activeTour)
      }
      setActiveTour(null)
    }
  }, [activeTour, user?.id])

  return (
    <TourContext.Provider
      value={{
        startTour,
        maybeStartTour,
        isRunning: run,
        showFirstLoginPrompt,
        handleFirstLoginStart,
        handleFirstLoginDismiss,
      }}
    >
      {children}
      <Joyride
        steps={steps}
        run={run}
        continuous
        options={{
          ...tourOptions,
          buttons: ['skip', 'back', 'close', 'primary'],
        }}
        styles={tourStyles}
        onEvent={handleEvent}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Done',
          next: 'Next',
          skip: 'Skip Tour',
        }}
      />
    </TourContext.Provider>
  )
}
